import {
  type AttendanceOnDemandIntegrationConfig,
  AttendanceVerificationMethod,
  type AttendanceVerificationMethodConfig,
  type MeshIntegrationConfig,
  type UkgIntegrationConfig,
  type UkgReadyIntegrationConfig,
} from "@clipboard-health/contract-backend-main";
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Stack } from "@mui/material";
import { type WorkplaceExternalIntegration } from "@src/appV2/ExternalIntegrations/api/useGetWorkplaceExternalIntegrations";
import { Text } from "@src/appV2/redesign/components/Text";
import { useCallback } from "react";

import {
  DEFAULT_KIOSK_VENDOR,
  KIOSK_VENDOR_OPTIONS,
  type KioskVendor,
  type NormalizedMeshIntegrationConfig,
  pinsToEnterStringSchema,
  timeclockLocationStringSchema,
  TUTORIAL_SECTION,
  type TutorialStep,
} from "../types";
import { SaveableTextField } from "./SaveableTextField";
import { TutorialStepsSection } from "./TutorialStepsSection";
import { UkgBusinessUnitNameField } from "./UkgBusinessUnitNameField";

const METHODS_WITH_TUTORIAL_AND_CREDENTIALS = new Set([
  AttendanceVerificationMethod.UKG_INTEGRATION,
  AttendanceVerificationMethod.UKG_READY_INTEGRATION,
  AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION,
]);

type ExternalIntegrationMethod =
  | UkgIntegrationConfig
  | UkgReadyIntegrationConfig
  | AttendanceOnDemandIntegrationConfig
  | MeshIntegrationConfig;

interface ExternalIntegrationConfigProps {
  method: ExternalIntegrationMethod;
  onSave: (config: AttendanceVerificationMethodConfig) => void;
  workplaceId: string;
  isCbhAdmin: boolean;
  externalIntegration?: WorkplaceExternalIntegration;
  parentExternalIntegration?: WorkplaceExternalIntegration;
}

const VENDOR_OPTIONS = [
  { value: AttendanceVerificationMethod.UKG_INTEGRATION, label: "UKG" },
  { value: AttendanceVerificationMethod.UKG_READY_INTEGRATION, label: "UKG Ready" },
  {
    value: AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION,
    label: "Attendance on Demand",
  },
  { value: AttendanceVerificationMethod.MESH_INTEGRATION, label: "Mesh" },
] as const;

function isKioskVendor(value: string): value is KioskVendor {
  return KIOSK_VENDOR_OPTIONS.some((option) => option.value === value);
}

interface LabeledRadioRowProps<Value extends string> {
  label: string;
  value: Value;
  options: ReadonlyArray<{ value: Value; label: string }>;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isOptionDisabled?: (optionValue: Value) => boolean;
}

function LabeledRadioRow<Value extends string>(props: LabeledRadioRowProps<Value>) {
  const { label, value, options, onChange, isOptionDisabled } = props;

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Text variant="body1" sx={{ minWidth: 60 }}>
        {label}
      </Text>
      <FormControl>
        <RadioGroup row value={value} onChange={onChange}>
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              disabled={isOptionDisabled?.(option.value) ?? false}
              control={<Radio size="small" />}
              label={option.label}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </Stack>
  );
}

export function ExternalIntegrationConfig(props: ExternalIntegrationConfigProps) {
  const {
    method,
    onSave,
    workplaceId,
    isCbhAdmin,
    externalIntegration,
    parentExternalIntegration,
  } = props;

  const handleVendorChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newType = event.target.value as (typeof VENDOR_OPTIONS)[number]["value"];

      const existingTutorial = method.tutorial ?? { stepsClockIn: [], stepsClockOut: [] };
      const existingTimeclockLocation = method.timeclockLocationString ?? "";
      const existingPinsToEnterString = method.pinsToEnterString ?? "";
      const existingKioskLocation =
        method.type === AttendanceVerificationMethod.MESH_INTEGRATION ? method.kioskLocation : "";
      const existingKioskVendor: KioskVendor =
        method.type === AttendanceVerificationMethod.MESH_INTEGRATION
          ? (method.kioskVendor ?? DEFAULT_KIOSK_VENDOR)
          : DEFAULT_KIOSK_VENDOR;

      const newConfig: AttendanceVerificationMethodConfig =
        newType === AttendanceVerificationMethod.MESH_INTEGRATION
          ? ({
              type: newType,
              isEnabled: method.isEnabled,
              timeclockLocationString: existingTimeclockLocation,
              pinsToEnterString: existingPinsToEnterString,
              tutorial: existingTutorial,
              kioskLocation: existingKioskLocation,
              kioskVendor: existingKioskVendor,
            } satisfies NormalizedMeshIntegrationConfig)
          : {
              type: newType,
              isEnabled: method.isEnabled,
              timeclockLocationString: existingTimeclockLocation,
              pinsToEnterString: existingPinsToEnterString,
              tutorial: existingTutorial,
            };

      onSave(newConfig);
    },
    [method, onSave]
  );

  const handleLocationSave = useCallback(
    async (value: string) => {
      onSave({
        ...method,
        timeclockLocationString: value,
      });
    },
    [method, onSave]
  );

  const handlePinsSave = useCallback(
    async (value: string) => {
      onSave({
        ...method,
        pinsToEnterString: value,
      });
    },
    [method, onSave]
  );

  const handleClockInStepsSave = useCallback(
    async (steps: TutorialStep[]) => {
      onSave({
        ...method,
        tutorial: {
          ...method.tutorial,
          stepsClockIn: steps,
        },
      });
    },
    [method, onSave]
  );

  const handleClockOutStepsSave = useCallback(
    async (steps: TutorialStep[]) => {
      onSave({
        ...method,
        tutorial: {
          ...method.tutorial,
          stepsClockOut: steps,
        },
      });
    },
    [method, onSave]
  );

  const handleRadioChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleVendorChange(event);
    },
    [handleVendorChange]
  );

  const handleKioskVendorChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (method.type !== AttendanceVerificationMethod.MESH_INTEGRATION) {
        return;
      }

      if (!isKioskVendor(event.target.value)) {
        return;
      }

      onSave({ ...method, kioskVendor: event.target.value });
    },
    [method, onSave]
  );

  return (
    <Stack spacing={3}>
      <Box>
        <LabeledRadioRow
          label="Vendor"
          value={method.type}
          options={VENDOR_OPTIONS}
          onChange={handleRadioChange}
          isOptionDisabled={(optionValue) => method.isEnabled && optionValue !== method.type}
        />
        {method.isEnabled && (
          <Text variant="caption" color="text.secondary" sx={{ display: "block", marginTop: 0.5 }}>
            Turn off this integration to switch vendors.
          </Text>
        )}
      </Box>

      {method.type === AttendanceVerificationMethod.MESH_INTEGRATION && (
        <Box>
          <LabeledRadioRow
            label="Kiosk vendor"
            value={method.kioskVendor ?? DEFAULT_KIOSK_VENDOR}
            options={KIOSK_VENDOR_OPTIONS}
            onChange={handleKioskVendorChange}
          />
        </Box>
      )}

      {isCbhAdmin && method.type === AttendanceVerificationMethod.UKG_INTEGRATION && (
        <UkgBusinessUnitNameField
          workplaceId={workplaceId}
          externalIntegration={externalIntegration}
          parentExternalIntegration={parentExternalIntegration}
        />
      )}

      <Box>
        <SaveableTextField
          label="Time clock location"
          value={method.timeclockLocationString ?? ""}
          placeholder="In the room with the copier"
          schema={timeclockLocationStringSchema}
          onSave={handleLocationSave}
        />
      </Box>
      {METHODS_WITH_TUTORIAL_AND_CREDENTIALS.has(method.type) && (
        <>
          <Box>
            <SaveableTextField
              label="Credentials template"
              value={method.pinsToEnterString ?? ""}
              placeholder="1234, 5678"
              schema={pinsToEnterStringSchema}
              onSave={handlePinsSave}
            />
          </Box>
          <TutorialStepsSection
            required
            title="Steps for clocking in"
            section={TUTORIAL_SECTION.TIMECLOCK_USE}
            steps={method.tutorial.stepsClockIn}
            onSaveStep={handleClockInStepsSave}
          />

          <TutorialStepsSection
            required
            title="Steps for clocking out"
            section={TUTORIAL_SECTION.TIMECLOCK_USE}
            steps={method.tutorial.stepsClockOut}
            onSaveStep={handleClockOutStepsSave}
          />
        </>
      )}
    </Stack>
  );
}
